import { buildApiUrl } from '../../../utils/api';

const requestJson = async ({ url, options = {}, timeoutMs = 10000, errorPrefix = 'Request failed' }) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {})
      }
    });

    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    const isJson = contentType.includes('application/json');
    const data = isJson ? await response.json().catch(() => ({})) : null;

    if (!response.ok) {
      const error = new Error(data?.error || `${errorPrefix} (${response.status})`);
      if (data && typeof data === 'object') {
        Object.assign(error, data);
      }
      throw error;
    }

    if (!isJson) {
      throw new Error(`${errorPrefix}: API returned non-JSON content`);
    }

    return data;
  } finally {
    clearTimeout(timeout);
  }
};

export const findBusinesses = async (searchText, timeoutMs = 8000) => {
  const data = await requestJson({
    url: buildApiUrl(`/api/search-businesses?query=${encodeURIComponent(searchText)}`),
    timeoutMs,
    errorPrefix: 'Search request failed'
  });
  return (data || []).slice(0, 6);
};

export const runGraderScan = async ({ business, attribution, turnstileToken = '', honeypot = '', timeoutMs = 60000 }) => {
  const headers = { 'Content-Type': 'application/json' };
  if (turnstileToken) {
    headers['cf-turnstile-response'] = turnstileToken;
  }

  const data = await requestJson({
    url: buildApiUrl('/api/grader/scan'),
    timeoutMs,
    errorPrefix: 'Scan request failed',
    options: {
      method: 'POST',
      headers,
      body: JSON.stringify({ business, attribution, website: honeypot })
    }
  });

  if (!data?.scanId || !data?.preview) {
    throw new Error('Invalid scan response payload');
  }

  return data;
};

export const fetchBusinessByPlaceId = (placeId, timeoutMs = 10000) => requestJson({
  url: buildApiUrl(`/api/place-details?placeid=${encodeURIComponent(placeId)}`),
  timeoutMs,
  errorPrefix: 'Place details request failed'
});

export const verifyUnlock = async (scanIdValue, tokenValue, timeoutMs = 15000) => {
  const data = await requestJson({
    url: buildApiUrl('/api/grader/unlock/verify'),
    timeoutMs,
    errorPrefix: 'Unlock request failed',
    options: {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ scanId: scanIdValue, unlockToken: tokenValue })
    }
  });

  if (!data?.unlocked || !data?.report) {
    throw new Error('Unlock verification failed');
  }

  return data.report;
};

export const startUnlockOtp = ({ scanIdValue, tokenValue, phoneValue }, timeoutMs = 15000) => requestJson({
  url: buildApiUrl('/api/grader/unlock/otp/start'),
  timeoutMs,
  errorPrefix: 'OTP start failed',
  options: {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      scanId: scanIdValue,
      unlockToken: tokenValue,
      phone: phoneValue
    })
  }
});

export const checkUnlockOtp = ({ scanIdValue, tokenValue, phoneValue, codeValue }, timeoutMs = 15000) => requestJson({
  url: buildApiUrl('/api/grader/unlock/otp/check'),
  timeoutMs,
  errorPrefix: 'OTP verify failed',
  options: {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      scanId: scanIdValue,
      unlockToken: tokenValue,
      phone: phoneValue,
      code: codeValue
    })
  }
});

export const submitProspectInfo = ({
  scanIdValue,
  tokenValue,
  name,
  email,
  relationship,
  optIn,
  consentAccepted
}, timeoutMs = 15000) => requestJson({
  url: buildApiUrl('/api/grader/unlock/prospect'),
  timeoutMs,
  errorPrefix: 'Prospect submit failed',
  options: {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      scanId: scanIdValue,
      unlockToken: tokenValue,
      name,
      email,
      relationship,
      optIn,
      consentAccepted,
      consentTextVersion: 'v1'
    })
  }
});